import { useState, useEffect, useMemo } from "react";
import AppBreadcrumbs from "../../../components/AppBreadcrumbs";
import * as services from "../../../services/saleng";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import SearchIcon from "@mui/icons-material/Search";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import { getAdjustedPurchaseItems } from "../../../utils/adjustPurchaseItems";

export default function SummaryIndividualPurchases() { // Renamed for clarity, matches file name
  const formatDate = (date) => date.toISOString().split("T")[0];
  const [startDate, setStartDate] = useState(() => {
    const date = new Date();
    date.setMonth(date.getMonth() - 1);
    return formatDate(date);
  });
  const [endDate, setEndDate] = useState(formatDate(new Date()));
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState([]);
  const [openSet, setOpenSet] = useState(() => new Set());
  const [search, setSearch] = useState("");
  const [shopId, setShopId] = useState();

  // state สำหรับการ sort
  const [orderBy, setOrderBy] = useState("name");
  const [order, setOrder] = useState("asc");

  const mapPersonType = {
    normal: "บุคคลธรรมดา",
    company: "นิติบุคคล",
  };

  // ---- ดึงข้อมูล ----
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const res = await services.getPurchaseTransactions({
          status: "payed",
          startDate,
          endDate,
          dateField: "paidAt",
        });

        const rawData = res.data?.data ?? [];
        if (rawData.length > 0) {
            setShopId(rawData[0].shopId);
        }
        const grouped = groupByCustomerAndMergeItems(rawData);
        setRows(grouped);
        setOpenSet(new Set());
      } catch (err) {
        console.error("fetch error", err);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [startDate, endDate]); // Removed shopId dependency to prevent infinite loops if shopId changes

  // ---- Filter ----
  const filteredRows = useMemo(() => {
    const q = (search || "").toLowerCase();
    if (!q) return rows;

    return rows.filter((row) => {
      const name = row.customer?.name?.toLowerCase() || "";
      const typeLabel = mapPersonType[row.customer?.personType] || "";
      const type = typeLabel.toLowerCase();
      const carList = (row.carNames || []).join(" ").toLowerCase();

      return name.includes(q) || type.includes(q) || carList.includes(q);
    });
  }, [rows, search]);

  // รวมค่าต่อแถว
  const getRowTotals = (row) => {
    const totalWeight = sum(row.items, "netWeight");
    const totalBeforeVat = sum(row.items, "amountBeforeVat");
    const totalVat = sum(row.items, "vat");
    const totalNet = sum(row.items, "netPrice");
    return { totalWeight, totalBeforeVat, totalVat, totalNet };
  };

  // ---- Sorted ----
  const sortedRows = useMemo(() => {
    const arr = [...filteredRows];

    const getSortValue = (row) => {
      const name = row.customer?.name ?? "";
      const personTypeLabel =
        mapPersonType[row.customer?.personType] ??
        row.customer?.personType ??
        "";
      const totals = getRowTotals(row);

      switch (orderBy) {
        case "name": return String(name).toLowerCase();
        case "personType": return String(personTypeLabel).toLowerCase();
        case "totalWeight": return Number(totals.totalWeight || 0);
        case "totalBeforeVat": return Number(totals.totalBeforeVat || 0);
        case "totalVat": return Number(totals.totalVat || 0);
        case "totalNet": return Number(totals.totalNet || 0);
        default: return "";
      }
    };

    arr.sort((a, b) => {
      const va = getSortValue(a);
      const vb = getSortValue(b);

      if (typeof va === "number" && typeof vb === "number") {
        return order === "asc" ? va - vb : vb - va;
      }

      const sa = String(va);
      const sb = String(vb);
      return order === "asc" ? sa.localeCompare(sb) : sb.localeCompare(sa);
    });

    return arr;
  }, [filteredRows, orderBy, order]);

  // ---- Summary ----
  const summary = useMemo(() => {
    let customerCount = filteredRows.length;
    let totalWeight = 0;
    let totalBeforeVat = 0;
    let totalVat = 0;
    let totalNet = 0;

    filteredRows.forEach((row) => {
      (row.items ?? []).forEach((i) => {
        totalWeight += i.netWeight ?? 0;
        totalBeforeVat += i.amountBeforeVat ?? 0;
        totalVat += i.vat ?? 0;
        totalNet += i.netPrice ?? 0;
      });
    });

    return { customerCount, totalWeight, totalBeforeVat, totalVat, totalNet };
  }, [filteredRows]);

  const getRowKey = (row, idx) =>
    String(row.customer?.id ?? row.customerId ?? idx);

  const toggleOpen = (key) => {
    setOpenSet((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  // --- CSV helper ---
  function exportToCSV(data, filename = "export.csv") {
    if (!data || data.length === 0) return;
    const headers = Object.keys(data[0]);
    const rows = data.map((row) =>
      headers.map((field) => `"${row[field] ?? ""}"`).join(",")
    );
    const csv = [headers.join(","), ...rows].join("\n");
    const blob = new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.setAttribute("download", filename);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }

  // --- Export ---
  const handleExportSummary = () => {
    const dataset = sortedRows.map((row) => {
      const { totalWeight, totalBeforeVat, totalVat, totalNet } = getRowTotals(row);
      const avgPrice = avg(row.items, "pricePerUnit");

      return {
        รายการ: row.customer?.name ?? "-",
        ประเภทลูกค้า: mapPersonType[row.customer?.personType] || "-",
        รถขนส่ง: (row.carNames || []).join(" | "),
        น้ำหนัก: Number(totalWeight).toFixed(2),
        หน่วย: "กก.",
        ราคาต่อหน่วย: Number(avgPrice).toFixed(2),
        ยอดก่อนรวมภาษี: Number(totalBeforeVat).toFixed(2),
        ยอดภาษี: Number(totalVat).toFixed(2),
        ยอดสุทธิ: Number(totalNet).toFixed(2),
      };
    });

    exportToCSV(dataset, `purchase_summary_by_customer_${startDate}_to_${endDate}.csv`);
  };

  const handleExportAllItems = () => {
    const dataset = [];
    sortedRows.forEach((row) => {
      const common = {
        ลูกค้า: row.customer?.name ?? "-",
        ประเภทลูกค้า: mapPersonType[row.customer?.personType] || "-",
        รถขนส่ง: (row.carNames || []).join(" | "),
      };

      (row.items || []).forEach((it) => {
        dataset.push({
          ...common,
          สินค้า: it.product?.title ?? "-",
          น้ำหนัก: Number(it.netWeight || 0).toFixed(2),
          หน่วย: it.unit ?? "กก.",
          ราคาต่อหน่วย: Number(it.pricePerUnit || 0).toFixed(2),
          ยอดก่อนรวมภาษี: Number(it.amountBeforeVat || 0).toFixed(2),
          ยอดภาษี: Number(it.vat || 0).toFixed(2),
          ยอดสุทธิ: Number(it.netPrice || 0).toFixed(2),
        });
      });
    });

    if (dataset.length === 0) return;
    exportToCSV(dataset, `purchase_all_items_${startDate}_to_${endDate}.csv`);
  };

  // เปลี่ยน sort
  const handleSort = (key) => {
    setOrder((prev) =>
      orderBy === key ? (prev === "asc" ? "desc" : "asc") : "asc"
    );
    setOrderBy(key);
  };

  return (
    <div className="min-h-screen bg-gray-50 p-4 font-sans">
      <div className="max-w-7xl mx-auto">
        <div className="mb-6">
          <AppBreadcrumbs />
        </div>

        {/* Header & Controls */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 mb-6">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <h1 className="text-2xl font-bold text-gray-800 flex items-center gap-2">
              <span className="w-1 h-8 bg-green-600 rounded-full"></span>
              สรุปยอดซื้อสินค้ารายบุคคล
            </h1>

            <div className="flex flex-wrap items-center gap-3">
               {/* Search */}
               <div className="relative">
                <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <input
                    type="text"
                    placeholder="ค้นหาชื่อ, ประเภท, รถ"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    className="pl-10 pr-4 py-2 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-transparent outline-none transition-all w-64"
                />
               </div>

              {/* Date Pickers */}
              <div className="flex items-center gap-2 bg-gray-50 px-3 py-2 rounded-xl border border-gray-200">
                <span className="text-sm font-medium text-gray-500 whitespace-nowrap">เริ่ม</span>
                <input
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  className="bg-transparent border-none focus:ring-0 text-gray-700 font-medium text-sm"
                />
              </div>

               <div className="flex items-center gap-2 bg-gray-50 px-3 py-2 rounded-xl border border-gray-200">
                <span className="text-sm font-medium text-gray-500 whitespace-nowrap">สิ้นสุด</span>
                <input
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  className="bg-transparent border-none focus:ring-0 text-gray-700 font-medium text-sm"
                />
              </div>
            </div>
          </div>
          
          <div className="mt-6 flex gap-2 justify-end">
                <button
                    onClick={handleExportSummary}
                    className="flex items-center gap-2 px-4 py-2 bg-white text-gray-700 border border-gray-200 rounded-xl hover:bg-gray-50 hover:border-gray-300 transition-all font-medium shadow-sm"
                >
                    <ContentCopyIcon fontSize="small" />
                    <span>Export ข้อมูลหลัก</span>
                </button>
                 <button
                    onClick={handleExportAllItems}
                    className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white border border-transparent rounded-xl hover:bg-green-700 transition-all font-medium shadow-sm hover:shadow-md"
                >
                    <ContentCopyIcon fontSize="small" />
                    <span>Export ข้อมูลย่อยทั้งหมด</span>
                </button>
          </div>
        </div>

        {/* Content */}
        {loading ? (
             <div className="flex justify-center items-center h-64">
                <div className="w-12 h-12 border-4 border-green-200 border-t-green-600 rounded-full animate-spin"></div>
             </div>
        ) : (
            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="w-full text-sm text-left">
                        <thead className="bg-gray-50 text-gray-600 font-semibold border-b border-gray-100">
                            <tr>
                                <th className="px-4 py-4 w-12"></th>
                                <th 
                                    className="px-4 py-4 cursor-pointer hover:bg-gray-100 transition-colors group select-none"
                                    onClick={() => handleSort("name")}
                                >
                                    <div className="flex items-center gap-1">
                                        รายการ
                                        {orderBy === "name" && (
                                            <span className="text-green-600">{order === "asc" ? "↑" : "↓"}</span>
                                        )}
                                    </div>
                                </th>
                                <th 
                                    className="px-4 py-4 cursor-pointer hover:bg-gray-100 transition-colors group select-none"
                                    onClick={() => handleSort("personType")}
                                >
                                     <div className="flex items-center gap-1">
                                        ประเภทลูกค้า
                                        {orderBy === "personType" && (
                                            <span className="text-green-600">{order === "asc" ? "↑" : "↓"}</span>
                                        )}
                                     </div>
                                </th>
                                <th className="px-4 py-4">รถขนส่ง</th>
                                <th 
                                    className="px-4 py-4 text-right cursor-pointer hover:bg-gray-100 transition-colors group select-none"
                                    onClick={() => handleSort("totalWeight")}
                                >
                                     <div className="flex items-center justify-end gap-1">
                                        น้ำหนัก
                                        {orderBy === "totalWeight" && (
                                            <span className="text-green-600">{order === "asc" ? "↑" : "↓"}</span>
                                        )}
                                     </div>
                                </th>
                                <th className="px-4 py-4">หน่วย</th>
                                <th className="px-4 py-4 text-right">ราคาต่อหน่วย</th>
                                <th 
                                    className="px-4 py-4 text-right cursor-pointer hover:bg-gray-100 transition-colors group select-none"
                                    onClick={() => handleSort("totalBeforeVat")}
                                >
                                    <div className="flex items-center justify-end gap-1">
                                        ยอดก่อนภาษี
                                        {orderBy === "totalBeforeVat" && (
                                            <span className="text-green-600">{order === "asc" ? "↑" : "↓"}</span>
                                        )}
                                    </div>
                                </th>
                                <th 
                                    className="px-4 py-4 text-right cursor-pointer hover:bg-gray-100 transition-colors group select-none"
                                    onClick={() => handleSort("totalVat")}
                                >
                                    <div className="flex items-center justify-end gap-1">
                                        ยอดภาษี
                                        {orderBy === "totalVat" && (
                                            <span className="text-green-600">{order === "asc" ? "↑" : "↓"}</span>
                                        )}
                                    </div>
                                </th>
                                <th 
                                    className="px-4 py-4 text-right cursor-pointer hover:bg-gray-100 transition-colors group select-none"
                                    onClick={() => handleSort("totalNet")}
                                >
                                    <div className="flex items-center justify-end gap-1">
                                        ยอดสุทธิ
                                        {orderBy === "totalNet" && (
                                            <span className="text-green-600">{order === "asc" ? "↑" : "↓"}</span>
                                        )}
                                    </div>
                                </th>
                                {shopId == 315 && <th className="px-4 py-4 text-right">Export Less</th>}
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-50">
                            {sortedRows.map((row, idx) => {
                                const key = getRowKey(row, idx);
                                const isOpen = openSet.has(key);
                                return (
                                    <RowGroup
                                        key={key}
                                        isOpen={isOpen}
                                        onToggle={() => toggleOpen(key)}
                                        row={row}
                                        shopId={shopId}
                                    />
                                );
                            })}
                        </tbody>
                        <tfoot className="bg-gray-50 font-bold text-gray-900 border-t border-gray-200">
                             <tr>
                                <td colSpan={4} className="px-4 py-4 text-right">
                                    รวมลูกค้า {summary.customerCount.toLocaleString("th-TH")} ราย
                                </td>
                                <td className="px-4 py-4 text-right">{formatNum(summary.totalWeight)}</td>
                                <td></td>
                                <td></td>
                                <td className="px-4 py-4 text-right">{formatNum(summary.totalBeforeVat)}</td>
                                <td className="px-4 py-4 text-right">{formatNum(summary.totalVat)}</td>
                                <td className="px-4 py-4 text-right text-green-700 text-lg">{formatNum(summary.totalNet)}</td>
                                {shopId == 315 && <td></td>}
                             </tr>
                        </tfoot>
                    </table>
                </div>
            </div>
        )}
      </div>
    </div>
  );
}

function RowGroup({ isOpen, onToggle, row, shopId }) {
  const totalWeight = sum(row.items, "netWeight");
  const avgPrice = avg(row.items, "pricePerUnit");
  const totalBeforeVat = sum(row.items, "amountBeforeVat");
  const totalVat = sum(row.items, "vat");
  const totalNet = sum(row.items, "netPrice");

  // Dialog State
  const [openReportDialog, setOpenReportDialog] = useState(false);
  const [downloadUrl, setDownloadUrl] = useState(null);
  const [popupStartDate, setPopupStartDate] = useState("");
  const [popupEndDate, setPopupEndDate] = useState("");
  const [selectRow, setSelectedRow] = useState(); //Kept for logic consistency, though 'row' prop is available

  const handleOpenReport = (r) => {
    setSelectedRow(r);
    setOpenReportDialog(true);
    setDownloadUrl(null); // Reset
    setPopupStartDate("");
    setPopupEndDate(""); 
  };

  const handleCloseReport = () => {
    setOpenReportDialog(false);
  };

  const handleGenerateReport = async () => {
    try {
        if (!selectRow) return;
        let payload = {
            startDate: popupStartDate,
            endDate: popupEndDate,
            customerId: selectRow.customerId
        };
        const res = await services.getLessReport(payload);
        setDownloadUrl(res.data.file);
    } catch (err) {
        console.error(err);
    }
  };

  return (
    <>
      <tr className="hover:bg-gray-50 transition-colors border-b border-gray-50 last:border-none">
        <td className="px-4 py-4">
            <button 
                onClick={onToggle}
                className="p-1 rounded-full hover:bg-gray-200 transition-colors text-gray-500"
            >
                {isOpen ? <KeyboardArrowDownIcon fontSize="small"/> : <KeyboardArrowRightIcon fontSize="small"/>}
            </button>
        </td>
        <td className="px-4 py-4 font-medium text-gray-800">{row.customer?.name ?? "-"}</td>
        <td className="px-4 py-4 text-gray-600">
             {row.customer?.personType === "company"
            ? "นิติบุคคล"
            : row.customer?.personType === "normal"
              ? "บุคคลธรรมดา"
              : "-"}
        </td>
        <td className="px-4 py-4">
             <div className="flex flex-wrap gap-1">
                {(row.carNames || []).map((car, i) => (
                    <span key={i} className="px-2 py-1 bg-gray-100 rounded text-xs text-gray-600">
                        {car}
                    </span>
                ))}
            </div>
        </td>
        <td className="px-4 py-4 text-right font-medium">{formatNum(totalWeight)}</td>
        <td className="px-4 py-4 text-gray-500 text-sm">กก.</td>
        <td className="px-4 py-4 text-right text-gray-600">{formatNum(avgPrice)}</td>
        <td className="px-4 py-4 text-right text-gray-600">{formatNum(totalBeforeVat)}</td>
        <td className="px-4 py-4 text-right text-gray-600">{formatNum(totalVat)}</td>
        <td className="px-4 py-4 text-right font-bold text-gray-800">{formatNum(totalNet)}</td>
        {shopId == 315 && (
            <td className="px-4 py-4 text-right">
                <button
                    onClick={() => handleOpenReport(row)}
                    className="text-xs text-blue-600 hover:text-blue-800 hover:underline"
                >
                    export รายงาน Less
                </button>
            </td>
        )}
      </tr>
      
      {/* Nested Table */}
      {isOpen && (
        <tr className="bg-gray-50/50">
            <td colSpan={shopId == 315 ? 11 : 10} className="px-4 py-4">
                 <div className="pl-12 pr-4">
                    <h4 className="text-sm font-bold text-gray-700 mb-3 flex items-center gap-2">
                        <span className="w-1 h-4 bg-green-500 rounded-full"></span>
                        รายการสินค้า
                    </h4>
                    <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
                        <table className="w-full text-sm text-left">
                            <thead className="bg-gray-50 text-gray-500 border-b border-gray-200">
                                <tr>
                                    <th className="px-4 py-2 font-medium">สินค้า</th>
                                    <th className="px-4 py-2 text-right font-medium">น้ำหนัก</th>
                                    <th className="px-4 py-2 font-medium">หน่วย</th>
                                    <th className="px-4 py-2 text-right font-medium">ราคาซื้อ/หน่วย</th>
                                    <th className="px-4 py-2 text-right font-medium">ยอดก่อนภาษี</th>
                                    <th className="px-4 py-2 text-right font-medium">ภาษี</th>
                                    <th className="px-4 py-2 text-right font-medium">ยอดรวม</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-100">
                                {(row.items || []).map((p, i) => (
                                    <tr key={i} className="hover:bg-gray-50">
                                        <td className="px-4 py-2 text-gray-800">{p.product?.title ?? "-"}</td>
                                        <td className="px-4 py-2 text-right text-gray-600">{formatNum(p.netWeight)}</td>
                                        <td className="px-4 py-2 text-gray-500">{p.unit ?? "กก."}</td>
                                        <td className="px-4 py-2 text-right text-gray-600">{formatNum(p.pricePerUnit)}</td>
                                        <td className="px-4 py-2 text-right text-gray-600">{formatNum(p.amountBeforeVat)}</td>
                                        <td className="px-4 py-2 text-right text-gray-600">{formatNum(p.vat)}</td>
                                        <td className="px-4 py-2 text-right font-medium text-gray-800">{formatNum(p.netPrice)}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                 </div>
            </td>
        </tr>
      )}

      {/* Custom Modal for Less Report */}
      {openReportDialog && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
            <div className="bg-white rounded-2xl shadow-lg max-w-sm w-full p-6 animate-scale-up">
                <h3 className="text-lg font-bold text-gray-800 mb-4">เลือกช่วงวันที่สำหรับรายงาน</h3>
                
                {!downloadUrl ? (
                    <div className="flex flex-col gap-4">
                        <div className="flex flex-col gap-1">
                            <label className="text-sm font-medium text-gray-700">วันที่เริ่มต้น</label>
                            <input 
                                type="date" 
                                value={popupStartDate}
                                onChange={(e) => setPopupStartDate(e.target.value)}
                                className="px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-green-500 outline-none"
                            />
                        </div>
                        <div className="flex flex-col gap-1">
                             <label className="text-sm font-medium text-gray-700">วันที่สิ้นสุด</label>
                            <input 
                                type="date" 
                                value={popupEndDate}
                                onChange={(e) => setPopupEndDate(e.target.value)}
                                className="px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-green-500 outline-none"
                            />
                        </div>
                    </div>
                ) : (
                    <div className="flex flex-col items-center gap-4 py-4">
                         <div className="w-12 h-12 bg-green-100 text-green-600 rounded-full flex items-center justify-center">
                            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path></svg>
                         </div>
                         <p className="text-gray-800 font-medium">รายงานพร้อมให้ดาวน์โหลด</p>
                         <a 
                            href={downloadUrl} 
                            target="_blank" 
                            rel="noreferrer"
                            className="w-full py-2 bg-green-600 text-white rounded-xl font-medium hover:bg-green-700 text-center transition-colors shadow-md shadow-green-200"
                        >
                            ดาวน์โหลดรายงาน (Excel)
                        </a>
                    </div>
                )}

                <div className="flex gap-3 justify-end mt-6">
                    <button 
                        onClick={handleCloseReport} 
                        className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg font-medium transition-colors"
                    >
                        ปิด
                    </button>
                    {!downloadUrl && (
                        <button 
                            onClick={handleGenerateReport}
                            disabled={!popupStartDate || !popupEndDate}
                            className={`px-4 py-2 text-white rounded-lg font-medium shadow-sm transition-all ${
                                !popupStartDate || !popupEndDate 
                                ? 'bg-gray-300 cursor-not-allowed' 
                                : 'bg-green-600 hover:bg-green-700 hover:shadow-md'
                            }`}
                        >
                            สร้างรายงาน
                        </button>
                    )}
                </div>
            </div>
        </div>
      )}
    </>
  );
}

function groupByCustomerAndMergeItems(data = []) {
  const grouped = {};

  data.forEach((row) => {
    const cid = row.customerId ?? row.customer?.id ?? "__unknown__";
    if (!grouped[cid]) {
      grouped[cid] = {
        customerId: cid,
        customer: row.customer,
        carNames: [],
        items: [],
      };
    }

    const carName = row.car?.license
      ? `${row.car?.type || ""} ${row.car.license}`.trim()
      : "ไม่มีข้อมูล";
    if (carName && !grouped[cid].carNames.includes(carName)) {
      grouped[cid].carNames.push(carName);
    }

    const items = getAdjustedPurchaseItems(row).map((item) => ({
      ...item,
      unit: item.unit || "กก.",
    }));

    grouped[cid].items.push(...items);
  });

  return Object.values(grouped).map((entry) => {
    const merged = {};
    (entry.items || []).forEach((item) => {
      const key = item.product?.title ?? "-";
      if (!merged[key]) {
        merged[key] = { ...item };
      } else {
        merged[key].netWeight += item.netWeight;
        merged[key].amountBeforeVat += item.amountBeforeVat;
        merged[key].vat += item.vat;
        merged[key].netPrice += item.netPrice;
      }
    });

    return {
      ...entry,
      items: Object.values(merged),
    };
  });
}

function formatNum(val) {
  return val != null
    ? Number(val).toLocaleString("th-TH", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })
    : "-";
}

function sum(arr, key) {
  return (arr || []).reduce((acc, item) => acc + (item?.[key] || 0), 0);
}

function avg(arr, key) {
  const a = arr || [];
  const total = a.reduce((acc, item) => acc + (item?.[key] || 0), 0);
  return a.length ? total / a.length : 0;
}
